import { openURL } from 'expo-linking';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import type { StyleProp, TextStyle } from 'react-native';
import type { TextProps } from 'react-native-paper';
import { Text } from 'react-native-paper';

import { AutoLinkSegment } from '@/shared/components/text/AutoLinkSegment';
import type { LinkType } from '@/shared/components/text/text';
import { segmentTextIntoAutoLinkParts } from '@/shared/components/text/utils';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

export type AutoLinkTextProps = Omit<TextProps<string>, 'children'> & {
  /**
   * Source plain text to scan for URLs/emails/phone numbers.
   */
  text?: string;
  wrapperStyle?: StyleProp<TextStyle>;
  /**
   * Custom style for link segments.
   */
  linkStyle?: StyleProp<TextStyle>;
  /**
   * Intercept link presses. Return true to indicate you handled it (prevent default open).
   */
  onLinkPress?: (info: {
    type: LinkType;
    value: string;
    href: string;
  }) => boolean | void;
};

/**
 * AutoLinkText
 * - Detects URLs, emails, and phone numbers in plain text.
 * - Renders them as clickable links using react-native-paper Text.
 * - No external dependencies.
 *
 * Props extend react-native-paper's TextProps (except children),
 * so you can pass variant, style, numberOfLines, selectable, etc.
 */
export const AutoLinkText: FC<AutoLinkTextProps> = ({
  text,
  wrapperStyle,
  linkStyle,
  onLinkPress,
  variant,
  style,
  ...restTextProps
}) => {
  const theme = useAppTheme();

  const segmentedParts = useMemo(
    () => segmentTextIntoAutoLinkParts(text),
    [text]
  );

  const handlePressLink = useCallback(
    async (info: { type: LinkType; value: string; href: string }) => {
      try {
        const handled = onLinkPress?.(info);
        if (handled === true) return;

        /*
            Todo: canOpen is false for emails
            const canOpen = await canOpenURL(info.href);
            if (canOpen) { await openURL(info.href);}
        */
        await openURL(info.href);
      } catch {
        console.error(
          '[AUTO_LINK_TEXT]: something went wrong while opening a auto detected link'
        );
      }
    },
    [onLinkPress]
  );

  const computedLinkStyle: StyleProp<TextStyle> = [
    { color: theme.colors.primary, textDecorationLine: 'underline' },
    linkStyle
  ];

  return (
    <Text variant={variant} {...restTextProps} style={wrapperStyle}>
      {segmentedParts.map((part) => (
        <AutoLinkSegment
          key={part.key}
          part={part}
          variant={variant}
          style={style}
          linkStyle={computedLinkStyle}
          onPressLink={handlePressLink}
        />
      ))}
    </Text>
  );
};
